import { PM_CHAT_HISTORY_LIMIT, type TaskContext } from "@project/shared";

export { PM_CHAT_HISTORY_LIMIT };

export function formatFileSize(bytes: number | undefined): string {
  if (bytes === undefined) return "";
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

export interface FileAttachment {
  fileName: string;
  mimeType: string;
  fileSize?: number;
  content?: string;
  contentEncoding?: string;
  downloadUrl?: string;
  fileId?: string;
}

export function formatChatFile(file: FileAttachment): string[] {
  const sizeStr = file.fileSize ? `, ${formatFileSize(file.fileSize)}` : "";
  if (file.content && file.contentEncoding === "utf-8") {
    return [
      `[Attached: ${file.fileName} (${file.mimeType}${sizeStr})]`,
      "```",
      file.content,
      "```",
    ];
  }
  if (!file.content) {
    return [`[Attached: ${file.fileName} (${file.mimeType}${sizeStr})]: ${file.downloadUrl}`];
  }
  if (file.content && file.contentEncoding === "base64") {
    return [
      `[Attached image: ${file.fileName} (${file.mimeType}${sizeStr}) — use get_attachment("${file.fileId}") to view]`,
    ];
  }
  return [`[Attached: ${file.fileName} (${file.mimeType}${sizeStr})]`];
}

export function formatTaskFile(file: FileAttachment): string[] {
  if (file.content && file.contentEncoding === "utf-8") {
    return [`\n### ${file.fileName} (${file.mimeType})`, "```", file.content, "```"];
  }
  if (file.content && file.contentEncoding === "base64") {
    const size = formatFileSize(file.fileSize);
    return [
      `- [Attached image: ${file.fileName} (${file.mimeType}${size ? `, ${size}` : ""}) — use get_attachment("${file.fileId}") to view]`,
    ];
  }
  if (!file.content) {
    return [`- **${file.fileName}** (${file.mimeType}): ${file.downloadUrl}`];
  }
  return [];
}

export function formatChatHistory(
  chatHistory: TaskContext["chatHistory"],
  limit?: number,
): string[] {
  const relevant = chatHistory.slice(-(limit ?? PM_CHAT_HISTORY_LIMIT));
  const parts = [`\n## Recent Chat Context`];
  for (const msg of relevant) {
    const sender = msg.userName ?? msg.role;
    parts.push(`[${sender}]: ${msg.content}`);
    if (msg.files?.length) {
      for (const file of msg.files) {
        parts.push(...formatChatFile(file));
      }
    }
  }
  return parts;
}

export function formatRepoRefs(repoRefs: NonNullable<TaskContext["repoRefs"]>): string[] {
  const parts: string[] = [];
  parts.push(`\n## Repository References`);
  for (const ref of repoRefs) {
    const icon = ref.refType === "folder" ? "folder" : "file";
    parts.push(`- [${icon}] \`${ref.path}\``);
  }
  return parts;
}

export function formatReferenceProjects(
  referenceProjects: NonNullable<TaskContext["referenceProjects"]>,
): string[] {
  const parts: string[] = [];
  parts.push(`\n## Reference Projects`);
  parts.push(
    `These sibling Conveyor projects have been shallow-cloned read-only into \`/workspaces/references/<slug>/\` for inspiration. You MAY grep/read them to compare approaches, but you MUST NOT modify them or commit anything from them into this task's repo.\n`,
  );
  for (const ref of referenceProjects) {
    const repo =
      ref.githubRepoOwner && ref.githubRepoName
        ? ` (${ref.githubRepoOwner}/${ref.githubRepoName})`
        : "";
    parts.push(`- **${ref.name}**${repo} — \`/workspaces/references/${ref.slug}/\``);
  }
  return parts;
}

export function formatProjectObjectives(
  objectives: NonNullable<TaskContext["projectObjectives"]>,
): string[] {
  const parts: string[] = [];
  parts.push(`\n## Project Objectives`);
  for (const obj of objectives) {
    const dates = `${obj.startDate.split("T")[0]} to ${obj.endDate.split("T")[0]}`;
    parts.push(`- **${obj.name}** (${dates})${obj.description ? ": " + obj.description : ""}`);
  }
  return parts;
}

export function formatRecentRelatedTasks(
  tasks: NonNullable<TaskContext["recentRelatedTasks"]>,
): string[] {
  const parts: string[] = [];
  parts.push(`\n## Recently Completed Related Tasks`);
  parts.push(
    `These tasks in the same domain were recently completed. Use them for context on recent changes and patterns.\n`,
  );
  for (const task of tasks) {
    const tags = task.tagNames.length > 0 ? ` [${task.tagNames.join(", ")}]` : "";
    const pr = task.githubPRUrl ? ` — PR: ${task.githubPRUrl}` : "";
    parts.push(`- **${task.title}**${tags}${pr}`);
  }
  return parts;
}

export function formatIncidents(incidents: NonNullable<TaskContext["incidents"]>): string[] {
  const parts: string[] = [];
  parts.push(`\n## Linked Incidents`);
  parts.push(
    `This task has linked incidents. Review them for context on the problem being addressed.\n`,
  );
  for (const inc of incidents) {
    const severity = inc.severity ? ` [${inc.severity}]` : "";
    const status = inc.status ? ` (${inc.status})` : "";
    parts.push(`### ${inc.title}${severity}${status}`);
    if (inc.description) parts.push(inc.description);
    if (inc.source) parts.push(`Source: ${inc.source}`);
  }
  return parts;
}
